import {
  GLOBAL_SEARCH_DEBOUNCE_MS,
  GLOBAL_SEARCH_GROUP_ORDER,
  GLOBAL_SEARCH_MIN_QUERY_LENGTH,
} from "@frontend-shared/constants";
import {
  EGlobalSearchItemType,
  type EGlobalSearchTab,
  EGlobalSearchTabBrowse,
  type IGlobalSearchGroup,
} from "@frontend-shared/types";
import {
  filterGlobalSearchGroupsByTab,
  getGlobalSearchTabDefinition,
  isGlobalSearchTabShowingType,
  sortGlobalSearchGroups,
} from "@frontend-shared/utils";
import { useMemo } from "react";

import { useDebounce } from "@/hooks/use-debounce";

import {
  EGlobalSearchTabStatus,
  type IGlobalSearchChatsState,
  type IGlobalSearchServerGroupsResult,
} from "../types";
import { useGlobalSearchBrowseGroups } from "./use-global-search-browse-groups";
import { useGlobalSearchChats } from "./use-global-search-chats";
import { useGlobalSearchClientGroups } from "./use-global-search-client-groups";
import { useGlobalSearchServerGroups } from "./use-global-search-server-groups";

const CHAT_RANK = GLOBAL_SEARCH_GROUP_ORDER.indexOf(EGlobalSearchItemType.CHAT);

const rankOf = (type: EGlobalSearchItemType): number => {
  const rank = GLOBAL_SEARCH_GROUP_ORDER.indexOf(type);
  return rank === -1 ? GLOBAL_SEARCH_GROUP_ORDER.length : rank;
};

interface IUseGlobalSearchResultsProps {
  query: string;
  tab: EGlobalSearchTab;
  onClose: () => void;
}

interface IGlobalSearchResults {
  // NOTE: Groups ranked before the chat sections (pages, settings) and after
  // them (everything else), in GLOBAL_SEARCH_GROUP_ORDER.
  leadingGroups: IGlobalSearchGroup[];
  trailingGroups: IGlobalSearchGroup[];
  chats: IGlobalSearchChatsState;
  server: IGlobalSearchServerGroupsResult;
  isSearching: boolean;
  // NOTE: Something besides the chats matched. The fallback actions do not
  // count — they are offered for every query, so they would otherwise hide
  // the "nothing found" state for good.
  hasOtherResults: boolean;
  // NOTE: NSAI-6853 — the active tab includes chats, so the chat sections
  // (with their own loading / empty / error states) are drawn.
  isChatsVisible: boolean;
  // NOTE: The /search/global loading / error line concerns the active tab.
  isServerStatusVisible: boolean;
  tabStatus: EGlobalSearchTabStatus;
}

interface IResolveTabStatusProps {
  isChatsVisible: boolean;
  isSearching: boolean;
  isBelowMinLength: boolean;
  isSettled: boolean;
  hasResults: boolean;
}

// NOTE: Tabs that show chats leave every state to the chat sections, which
// already speak for the whole palette when nothing else matched.
const resolveTabStatus = ({
  isChatsVisible,
  isSearching,
  isBelowMinLength,
  isSettled,
  hasResults,
}: IResolveTabStatusProps): EGlobalSearchTabStatus => {
  if (isChatsVisible || hasResults) return EGlobalSearchTabStatus.NONE;
  // NOTE: Browsing, the tab is empty only once its browse request answered.
  if (!isSearching) {
    return isSettled
      ? EGlobalSearchTabStatus.IDLE
      : EGlobalSearchTabStatus.NONE;
  }
  if (isBelowMinLength) return EGlobalSearchTabStatus.MIN_LENGTH;
  return isSettled ? EGlobalSearchTabStatus.EMPTY : EGlobalSearchTabStatus.NONE;
};

// NOTE: NSAI-6589 — every source of the palette merged into one ordered list.
// The chat sections are not IGlobalSearchItem groups (they keep their own
// rows, paging and states), so the list is split around the CHAT slot.
// NSAI-6853 — the active tab narrows that list; with an empty box a category
// tab lists what it can browse instead (useGlobalSearchBrowseGroups).
export const useGlobalSearchResults = ({
  query,
  tab,
  onClose,
}: IUseGlobalSearchResultsProps): IGlobalSearchResults => {
  const debouncedQuery = useDebounce(query, GLOBAL_SEARCH_DEBOUNCE_MS);
  const trimmedQuery = query.trim();
  const isSearching = trimmedQuery.length > 0;
  const definition = getGlobalSearchTabDefinition(tab);
  const chats = useGlobalSearchChats({ query, debouncedQuery, onClose });
  const clientGroups = useGlobalSearchClientGroups({ query, isEnabled: true });
  const server = useGlobalSearchServerGroups({ debouncedQuery });
  const browse = useGlobalSearchBrowseGroups({
    tab,
    isEnabled: !isSearching,
  });
  const isChatsVisible =
    isGlobalSearchTabShowingType({ tab, type: EGlobalSearchItemType.CHAT }) &&
    (isSearching || definition.browse === EGlobalSearchTabBrowse.CHATS);

  const { leadingGroups, trailingGroups, hasOtherResults } = useMemo(() => {
    const groups = isSearching
      ? filterGlobalSearchGroupsByTab({
          tab,
          groups: sortGlobalSearchGroups([...clientGroups, ...server.groups]),
        })
      : browse.groups;
    return {
      leadingGroups: groups.filter((group) => rankOf(group.type) < CHAT_RANK),
      trailingGroups: groups.filter((group) => rankOf(group.type) > CHAT_RANK),
      hasOtherResults: groups.some(
        (group) => group.type !== EGlobalSearchItemType.ACTION,
      ),
    };
  }, [isSearching, tab, clientGroups, server.groups, browse.groups]);

  // NOTE: NSAI-6853 — the loading / error line belongs to a search the user
  // typed. Browsing (an empty box) is a courtesy: while it loads the tab stays
  // quiet, and if it fails the tab simply asks for a query — never an error
  // for something the user did not ask for.
  const isServerStatusVisible = isSearching && definition.hasServerResults;
  const isSettled = isSearching
    ? query === debouncedQuery &&
      !(isServerStatusVisible && (server.isLoading || server.isError))
    : !browse.isLoading;

  return {
    leadingGroups,
    trailingGroups,
    chats,
    server,
    isSearching,
    hasOtherResults,
    isChatsVisible,
    isServerStatusVisible,
    tabStatus: resolveTabStatus({
      isChatsVisible,
      isSearching,
      isBelowMinLength: trimmedQuery.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH,
      isSettled,
      hasResults: leadingGroups.length > 0 || trailingGroups.length > 0,
    }),
  };
};
